/*
 * Display-only client-side currency converter (v2).
 *
 * Cart/checkout still happen in the shop's base currency. For real
 * multi-currency checkout use Shopify Markets / Shopify Payments.
 *
 * Strategy:
 *   1. Find price containers via class heuristics (.price, [data-price], etc.).
 *   2. For each text node inside, try in order:
 *        a) money_with_currency_format pattern  ("1,234.00 AED")
 *        b) money_format pattern                ("AED 1,234.00" if it has prefix)
 *        c) bare numeric text inside a money/price wrapper ("1,234.00")
 *   3. Replace just the matched substring/text, preserving surrounding markup.
 *
 * Rates: https://open.er-api.com (no key) cached 12h in localStorage.
 */
(() => {
  const STORAGE_KEY = 'glDisplayCurrency';
  const STORAGE_SOURCE_KEY = 'glDisplayCurrencySource'; // 'user' | 'geo'
  const GEO_KEY = 'glGeoCurrency';
  const GEO_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
  const RATES_KEY = 'glDisplayCurrencyRates';
  const RATES_TTL_MS = 12 * 60 * 60 * 1000;
  const FETCH_TIMEOUT_MS = 6000;
  const GEO_ENDPOINTS = [
    // Each returns ISO currency code under various keys.
    { url: 'https://ipwho.is/?fields=currency', parse: (d) => d && d.currency && d.currency.code },
    { url: 'https://ipapi.co/json/', parse: (d) => d && d.currency },
  ];
  const FX_ENDPOINTS = [
    // Each takes a base currency and returns { base, rates: { CODE: factor } }.
    {
      url: (base) => `https://open.er-api.com/v6/latest/${encodeURIComponent(base)}`,
      parse: (d) => (d && d.result === 'success' ? d.rates : null),
    },
    {
      url: (base) => `https://api.frankfurter.dev/v1/latest?from=${encodeURIComponent(base)}`,
      parse: (d, base) => {
        if (!d || !d.rates) return null;
        const r = Object.assign({}, d.rates);
        r[base] = 1; // frankfurter omits the base; add it explicitly.
        return r;
      },
    },
  ];

  // Primary country per currency code, used to label dropdown options as
  // "Country Name (CODE)". Codes not listed fall back to just the code.
  const CURRENCY_COUNTRIES = {
    AED: 'United Arab Emirates', USD: 'United States', AFN: 'Afghanistan', ALL: 'Albania', AMD: 'Armenia',
    ANG: 'Curaçao', AOA: 'Angola', ARS: 'Argentina', AUD: 'Australia', AWG: 'Aruba',
    AZN: 'Azerbaijan', BAM: 'Bosnia & Herzegovina', BBD: 'Barbados', BDT: 'Bangladesh',
    BGN: 'Bulgaria', BHD: 'Bahrain', BIF: 'Burundi', BMD: 'Bermuda', BND: 'Brunei',
    BOB: 'Bolivia', BRL: 'Brazil', BSD: 'Bahamas', BTN: 'Bhutan', BWP: 'Botswana',
    BYN: 'Belarus', BZD: 'Belize', CAD: 'Canada', CDF: 'DR Congo', CHF: 'Switzerland',
    CLP: 'Chile', CNY: 'China', COP: 'Colombia', CRC: 'Costa Rica', CUP: 'Cuba',
    CVE: 'Cape Verde', CZK: 'Czech Republic', DJF: 'Djibouti', DKK: 'Denmark',
    DOP: 'Dominican Republic', DZD: 'Algeria', EGP: 'Egypt', ERN: 'Eritrea',
    ETB: 'Ethiopia', EUR: 'European Union', FJD: 'Fiji', FKP: 'Falkland Islands',
    FOK: 'Faroe Islands', GBP: 'United Kingdom', GEL: 'Georgia', GGP: 'Guernsey',
    GHS: 'Ghana', GIP: 'Gibraltar', GMD: 'Gambia', GNF: 'Guinea', GTQ: 'Guatemala',
    GYD: 'Guyana', HKD: 'Hong Kong', HNL: 'Honduras', HRK: 'Croatia', HTG: 'Haiti',
    HUF: 'Hungary', IDR: 'Indonesia', ILS: 'Israel', IMP: 'Isle of Man', INR: 'India',
    IQD: 'Iraq', IRR: 'Iran', ISK: 'Iceland', JEP: 'Jersey', JMD: 'Jamaica',
    JOD: 'Jordan', JPY: 'Japan', KES: 'Kenya', KGS: 'Kyrgyzstan', KHR: 'Cambodia',
    KID: 'Kiribati', KMF: 'Comoros', KRW: 'South Korea', KWD: 'Kuwait',
    KYD: 'Cayman Islands', KZT: 'Kazakhstan', LAK: 'Laos', LBP: 'Lebanon',
    LKR: 'Sri Lanka', LRD: 'Liberia', LSL: 'Lesotho', LYD: 'Libya', MAD: 'Morocco',
    MDL: 'Moldova', MGA: 'Madagascar', MKD: 'North Macedonia', MMK: 'Myanmar',
    MNT: 'Mongolia', MOP: 'Macau', MRU: 'Mauritania', MUR: 'Mauritius', MVR: 'Maldives',
    MWK: 'Malawi', MXN: 'Mexico', MYR: 'Malaysia', MZN: 'Mozambique', NAD: 'Namibia',
    NGN: 'Nigeria', NIO: 'Nicaragua', NOK: 'Norway', NPR: 'Nepal', NZD: 'New Zealand',
    OMR: 'Oman', PAB: 'Panama', PEN: 'Peru', PGK: 'Papua New Guinea', PHP: 'Philippines',
    PKR: 'Pakistan', PLN: 'Poland', PYG: 'Paraguay', QAR: 'Qatar', RON: 'Romania',
    RSD: 'Serbia', RUB: 'Russia', RWF: 'Rwanda', SAR: 'Saudi Arabia',
    SBD: 'Solomon Islands', SCR: 'Seychelles', SDG: 'Sudan', SEK: 'Sweden',
    SGD: 'Singapore', SHP: 'Saint Helena', SLE: 'Sierra Leone', SLL: 'Sierra Leone',
    SOS: 'Somalia', SRD: 'Suriname', SSP: 'South Sudan', STN: 'São Tomé & Príncipe',
    SYP: 'Syria', SZL: 'Eswatini', THB: 'Thailand', TJS: 'Tajikistan',
    TMT: 'Turkmenistan', TND: 'Tunisia', TOP: 'Tonga', TRY: 'Turkey',
    TTD: 'Trinidad & Tobago', TVD: 'Tuvalu', TWD: 'Taiwan', TZS: 'Tanzania',
    UAH: 'Ukraine', UGX: 'Uganda', UYU: 'Uruguay',
    UZS: 'Uzbekistan', VES: 'Venezuela', VND: 'Vietnam', VUV: 'Vanuatu', WST: 'Samoa',
    XAF: 'Central Africa', XCD: 'East Caribbean', XDR: 'IMF', XOF: 'West Africa',
    XPF: 'French Polynesia', YER: 'Yemen', ZAR: 'South Africa', ZMW: 'Zambia',
    ZWL: 'Zimbabwe',
  };

  function optionLabel(code) {
    const name = CURRENCY_COUNTRIES[code];
    return name ? `${name} (${code})` : code;
  }

  const cfg = (window.themeVariables && window.themeVariables.settings) || {};
  const BASE = (cfg.shopCurrency || 'AED').toUpperCase();
  const MONEY_FORMAT = cfg.moneyFormat || '{{amount}}';
  const MONEY_WITH_CURRENCY_FORMAT = cfg.moneyWithCurrencyFormat || MONEY_FORMAT;

  // ----- Pattern building -----------------------------------------------------
  function buildPattern(format, { allowUnbounded = false } = {}) {
    if (!format) return null;
    const clean = String(format).replace(/<[^>]+>/g, '').trim();
    const tokenRe = /\{\{\s*\w+\s*\}\}/;
    const m = clean.match(tokenRe);
    if (!m) return null;
    const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const before = clean.slice(0, m.index).trim();
    const after = clean.slice(m.index + m[0].length).trim();
    if (!allowUnbounded && !before && !after) return null;
    const src = escape(before) + (before ? '\\s*' : '') +
                '([\\d.,]+)' +
                (after ? '\\s*' : '') + escape(after);
    return new RegExp(src, 'g');
  }

  const PATTERN_WITH_CURRENCY = buildPattern(MONEY_WITH_CURRENCY_FORMAT);
  const PATTERN_MONEY = buildPattern(MONEY_FORMAT); // null if just {{amount}}

  // ----- Number parsing / formatting ------------------------------------------
  function parseAmount(s) {
    if (typeof s !== 'string') return NaN;
    if (/,\d{1,2}$/.test(s) && /\./.test(s)) {
      return parseFloat(s.replace(/\./g, '').replace(',', '.'));
    }
    if (/^\d+,\d{1,2}$/.test(s)) return parseFloat(s.replace(',', '.'));
    return parseFloat(s.replace(/,/g, ''));
  }

  function formatConverted(amount, code) {
    const rounded = Math.round(amount);
    try {
      // Use the ISO code (e.g. "CAD 652") instead of localized symbols
      // ("CA$652") so the currency is always unambiguous to the shopper.
      const parts = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: code,
        currencyDisplay: 'code',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }).formatToParts(rounded);
      // Drop the currency token (Intl emits it) and rebuild as "CODE 1,234"
      // regardless of the locale's preferred placement.
      const number = parts
        .filter((p) => p.type !== 'currency' && p.type !== 'literal')
        .map((p) => p.value)
        .join('');
      return code + ' ' + number;
    } catch (_) {
      return code + ' ' + rounded.toLocaleString();
    }
  }

  // ----- FX rates --------------------------------------------------------------
  function fetchWithTimeout(url, ms) {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), ms);
    return fetch(url, { cache: 'no-store', signal: ctrl.signal }).finally(() => clearTimeout(t));
  }

  async function loadRates() {
    try {
      const cached = JSON.parse(localStorage.getItem(RATES_KEY) || 'null');
      if (cached && cached.base === BASE &&
          Date.now() - cached.fetchedAt < RATES_TTL_MS && cached.rates) {
        return cached.rates;
      }
    } catch (_) {}

    for (const ep of FX_ENDPOINTS) {
      try {
        const res = await fetchWithTimeout(ep.url(BASE), FETCH_TIMEOUT_MS);
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const data = await res.json();
        const rates = ep.parse(data, BASE);
        if (!rates || typeof rates !== 'object') throw new Error('Bad payload');
        try {
          localStorage.setItem(RATES_KEY, JSON.stringify({
            base: BASE, fetchedAt: Date.now(), rates,
          }));
        } catch (_) {}
        return rates;
      } catch (e) {
        console.warn('[currency-converter] FX endpoint failed:', e && e.message);
      }
    }
    return null;
  }

  // Kick off a single shared rates promise as soon as the script runs so the
  // user never waits on first interaction.
  let ratesPromise = null;
  function ensureRates() {
    if (rates) return Promise.resolve(rates);
    if (!ratesPromise) ratesPromise = loadRates().then((r) => { rates = r; return r; });
    return ratesPromise;
  }

  // ----- Geo-IP currency detection -------------------------------------------
  // Returns the user's likely currency code from their IP, or null. Cached for
  // GEO_TTL_MS in localStorage. Only used when the user hasn't picked a
  // currency manually (a manual pick is recorded with source = 'user').
  async function detectGeoCurrency() {
    try {
      const cached = JSON.parse(localStorage.getItem(GEO_KEY) || 'null');
      if (cached && cached.code && Date.now() - cached.fetchedAt < GEO_TTL_MS) {
        return cached.code;
      }
    } catch (_) {}

    for (const ep of GEO_ENDPOINTS) {
      try {
        const res = await fetchWithTimeout(ep.url, FETCH_TIMEOUT_MS);
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const data = await res.json();
        const code = (ep.parse(data) || '').toUpperCase();
        if (!/^[A-Z]{3}$/.test(code)) throw new Error('Bad geo payload');
        try {
          localStorage.setItem(GEO_KEY, JSON.stringify({ code, fetchedAt: Date.now() }));
        } catch (_) {}
        return code;
      } catch (e) {
        console.warn('[currency-converter] Geo endpoint failed:', e && e.message);
      }
    }
    return null;
  }

  // ----- DOM walk --------------------------------------------------------------
  const PRICE_SELECTOR = [
    '[class*="price"]',
    '[data-price]',
    '.cart-drawer__button-price',
    '.ecom-predictive-search__item-price',
    '.ecom-product-single__price--sale',
    '.ecom-product-single__price-normal',
    '.money',
    '.transcy-money',
  ].join(', ');

  const SKIP_SELECTOR = '.no-currency-convert, currency-switcher';

  function isBareNumericMoneyNode(node) {
    if (!node || node.nodeType !== 3) return false;
    const trimmed = node.nodeValue.trim();
    if (!/^[\d.,]+$/.test(trimmed)) return false;
    const parent = node.parentElement;
    if (!parent) return false;
    const ownText = parent.textContent.trim();
    return ownText === trimmed;
  }

  const textOriginals = new WeakMap();
  let target = (safeRead(STORAGE_KEY) || BASE).toUpperCase();
  let rates = null;
  let observer = null;

  function safeRead(k) { try { return localStorage.getItem(k); } catch (_) { return null; } }

  function eachPriceTextNode(root, callback) {
    if (!root || !root.querySelectorAll) return;
    const containers = root.matches && root.matches(PRICE_SELECTOR)
      ? [root]
      : Array.from(root.querySelectorAll(PRICE_SELECTOR));

    containers.forEach((container) => {
      if (container.closest(SKIP_SELECTOR)) return;
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
        acceptNode(n) {
          if (!n.nodeValue || !n.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
          if (n.parentElement && n.parentElement.closest(SKIP_SELECTOR)) {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_ACCEPT;
        },
      });
      let n;
      while ((n = walker.nextNode())) callback(n);
    });
  }

  function snapshotOriginals(root) {
    eachPriceTextNode(root || document.body, (node) => {
      if (!textOriginals.has(node)) textOriginals.set(node, node.nodeValue);
    });
  }

  function convertText(original, factor) {
    let out = original;
    let matched = false;

    if (PATTERN_WITH_CURRENCY) {
      const re = new RegExp(PATTERN_WITH_CURRENCY.source, 'g');
      out = out.replace(re, (m, num) => {
        const a = parseAmount(num);
        if (isNaN(a)) return m;
        matched = true;
        return formatConverted(a * factor, target);
      });
      if (matched) return out;
    }

    if (PATTERN_MONEY) {
      const re = new RegExp(PATTERN_MONEY.source, 'g');
      out = out.replace(re, (m, num) => {
        const a = parseAmount(num);
        if (isNaN(a)) return m;
        matched = true;
        return formatConverted(a * factor, target);
      });
    }
    return matched ? out : null;
  }

  function applyConversion(root) {
    const factor = target === BASE ? 1 : (rates && rates[target]);
    if (target !== BASE && !factor) return;

    eachPriceTextNode(root || document.body, (node) => {
      if (!textOriginals.has(node)) textOriginals.set(node, node.nodeValue);
      const original = textOriginals.get(node);

      if (target === BASE) {
        // Strip trailing ".00" / ",00" from the base-currency original so AED
        // shows "1,750 AED" instead of "1,750.00 AED".
        const stripped = stripZeroDecimals(original);
        if (node.nodeValue !== stripped) node.nodeValue = stripped;
        return;
      }

      const replaced = convertText(original, factor);
      if (replaced != null) {
        if (node.nodeValue !== replaced) node.nodeValue = replaced;
        return;
      }

      if (isBareNumericMoneyNode(node)) {
        const a = parseAmount(original.trim());
        if (!isNaN(a)) {
          const next = original.replace(/[\d.,]+/, () => formatConverted(a * factor, target));
          if (node.nodeValue !== next) node.nodeValue = next;
        }
      }
    });
  }

  // Removes ".00" or ",00" from a number that has thousand separators,
  // e.g. "1,750.00" -> "1,750" but leaves "19.99" alone.
  function stripZeroDecimals(str) {
    return String(str).replace(/(\d{1,3}(?:[.,]\d{3})*)([.,])00\b/g, '$1');
  }

  function startObserver() {
    if (observer) return;
    observer = new MutationObserver((mutations) => {
      let touched = false;
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType === 1) { snapshotOriginals(n); touched = true; }
        });
      }
      if (touched) applyConversion();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ----- Public API -----------------------------------------------------------
  async function setCurrency(code, opts) {
    const source = (opts && opts.source) || 'user';
    target = (code || BASE).toUpperCase();
    try {
      localStorage.setItem(STORAGE_KEY, target);
      localStorage.setItem(STORAGE_SOURCE_KEY, source);
    } catch (_) {}
    if (target !== BASE && !rates) {
      document.dispatchEvent(new CustomEvent('display-currency:loading'));
      await ensureRates();
    }
    if (target !== BASE && !rates) {
      // FX never resolved — surface a console error and leave prices alone.
      console.error('[currency-converter] FX rates unavailable; cannot convert to', target);
      document.dispatchEvent(new CustomEvent('display-currency:error'));
      return;
    }
    applyConversion();
    document.dispatchEvent(new CustomEvent('display-currency:change', {
      detail: { currency: target },
    }));
  }

  class CurrencySwitcher extends HTMLElement {
    connectedCallback() {
      if (this._mounted) return;
      this._mounted = true;
      if (this.getAttribute('data-style') === 'toggle') {
        this._mountPopover();
      } else {
        this._mountSelect();
      }
    }

    _mountPopover() {
      const listbox = this.querySelector('[data-currency-options]');
      const toggleLabel = this.querySelector('[data-currency-toggle-label]');
      if (!listbox || !toggleLabel) return;

      const renderLabel = (code) => { toggleLabel.textContent = code; };
      const markSelected = (code) => {
        listbox.querySelectorAll('button[data-currency-value]').forEach((b) => {
          b.setAttribute('aria-selected', b.getAttribute('data-currency-value') === code ? 'true' : 'false');
        });
      };

      renderLabel(target);

      const handleClick = (code) => {
        renderLabel(code);
        markSelected(code);
        // On mobile (md-max) the popover is moved to <body>, so a local
        // querySelector inside this element won't find it. Look up by ID and
        // use multiple close strategies for resilience.
        const popoverId = this.getAttribute('data-popover-id');
        const popover = (popoverId && document.getElementById(popoverId))
          || this.querySelector('x-popover');
        if (popover) {
          // 1. Native API.
          if (typeof popover.hide === 'function') {
            try { popover.hide(); } catch (_) {}
          }
          // 2. Force-close event the DialogElement listens for.
          try {
            popover.dispatchEvent(new CustomEvent('dialog:force-close', { bubbles: true }));
          } catch (_) {}
          // 3. listbox:select event — the popover supports close-on-listbox-select.
          try {
            popover.dispatchEvent(new CustomEvent('listbox:select', { bubbles: true }));
          } catch (_) {}
          // 4. Last-ditch attribute removal.
          if (popover.hasAttribute('open')) popover.removeAttribute('open');
        }
        setCurrency(code);
      };

      // Bind delegated click for any options (including the seed BASE button).
      listbox.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-currency-value]');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        handleClick(btn.getAttribute('data-currency-value'));
      });

      ensureRates().then((r) => {
        // Clear seed and rebuild full list.
        listbox.innerHTML = '';
        const others = (r ? Object.keys(r) : []).filter((c) => c !== BASE);
        // Always pin the checkout currency (BASE) at the top, then alphabetize
        // the rest by country name.
        others.sort((a, b) => optionLabel(a).localeCompare(optionLabel(b)));
        const codes = [BASE, ...others];
        codes.forEach((code) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'popover__value-option h-stack gap-2.5';
          btn.setAttribute('role', 'option');
          btn.setAttribute('data-currency-value', code);
          btn.setAttribute('aria-selected', code === target ? 'true' : 'false');
          const span = document.createElement('span');
          span.textContent = optionLabel(code);
          btn.appendChild(span);
          listbox.appendChild(btn);
        });
      });

      // Keep label in sync if currency changes elsewhere.
      document.addEventListener('display-currency:change', (e) => {
        const code = e.detail && e.detail.currency;
        if (code) { renderLabel(code); markSelected(code); }
      });
    }

    _mountSelect() {
      const select = document.createElement('select');
      const customClass = this.getAttribute('data-select-class');
      select.className = customClass || 'currency-switcher__select no-currency-convert';
      select.setAttribute('aria-label', 'Display currency');

      // Render BASE immediately so the dropdown is never empty.
      const baseOpt = document.createElement('option');
      baseOpt.value = BASE;
      baseOpt.textContent = optionLabel(BASE);
      if (target === BASE) baseOpt.selected = true;
      select.appendChild(baseOpt);

      // Loading placeholder while we await FX rates.
      const loadingOpt = document.createElement('option');
      loadingOpt.value = '';
      loadingOpt.textContent = 'Loading…';
      loadingOpt.disabled = true;
      select.appendChild(loadingOpt);

      select.addEventListener('change', (e) => {
        if (!e.target.value) return;
        setCurrency(e.target.value);
      });
      this.appendChild(select);

      ensureRates().then((r) => {
        select.removeChild(loadingOpt);
        if (!r) {
          const errOpt = document.createElement('option');
          errOpt.value = '';
          errOpt.textContent = 'Rates unavailable';
          errOpt.disabled = true;
          select.appendChild(errOpt);
          return;
        }
        // Pin the checkout currency (BASE) at the top — it's already the first
        // <option>. Alphabetize the rest by country name.
        const codes = Object.keys(r)
          .filter((c) => c !== BASE)
          .sort((a, b) => optionLabel(a).localeCompare(optionLabel(b)));
        codes.forEach((c) => {
          const opt = document.createElement('option');
          opt.value = c;
          opt.textContent = optionLabel(c);
          if (c === target) opt.selected = true;
          select.appendChild(opt);
        });
      });
    }
  }

  function init() {
    snapshotOriginals();
    // Always run once so base-currency prices have their trailing .00 stripped.
    applyConversion();
    ensureRates().then(() => {
      if (target !== BASE) applyConversion();
    });
    startObserver();
    if (!customElements.get('currency-switcher')) {
      customElements.define('currency-switcher', CurrencySwitcher);
    }

    // Geo-IP default: if the user has never explicitly picked a currency,
    // detect their country's currency and apply it. A manual pick is recorded
    // with source='user' and always wins; we never override it.
    const storedSource = safeRead(STORAGE_SOURCE_KEY);
    if (storedSource !== 'user') {
      detectGeoCurrency().then(async (geoCode) => {
        if (!geoCode || geoCode === target) return;
        if (safeRead(STORAGE_SOURCE_KEY) === 'user') return; // race guard
        const r = await ensureRates();
        // Only switch if we actually have a rate for that currency.
        if (geoCode === BASE || (r && r[geoCode])) {
          setCurrency(geoCode, { source: 'geo' });
        }
      });
    }

    // Intercept the Shopify country selector so it acts as a display-only
    // currency picker. Without Shopify Markets / Payments multi-currency
    // configured, posting to /localization causes a redirect loop and never
    // actually changes the displayed currency.
    document.addEventListener(
      'click',
      (e) => {
        const btn = e.target && e.target.closest && e.target.closest('button[name="country_code"][data-currency]');
        if (!btn) return;
        const code = btn.getAttribute('data-currency');
        if (!code) return;
        e.preventDefault();
        e.stopPropagation();
        // Reflect selection in the popover.
        const list = btn.closest('[data-currency-router]');
        if (list) {
          list.querySelectorAll('button[aria-selected]').forEach((b) =>
            b.setAttribute('aria-selected', b === btn ? 'true' : 'false')
          );
        }
        // Close the popover if it's open.
        const popover = btn.closest('x-popover, [popover]');
        if (popover) {
          if (typeof popover.hide === 'function') {
            try { popover.hide(); } catch (_) {}
          } else if (popover.hasAttribute('open')) {
            popover.removeAttribute('open');
          }
        }
        setCurrency(code);
      },
      true // capture so we beat the form submission handler
    );
  }

  window.GLCurrencyConverter = {
    setCurrency,
    getCurrency: () => target,
    getBase: () => BASE,
    getPatterns: () => ({
      withCurrency: PATTERN_WITH_CURRENCY && PATTERN_WITH_CURRENCY.source,
      money: PATTERN_MONEY && PATTERN_MONEY.source,
    }),
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
